import { useState } from 'react';
import { Shell } from '../components/Layout.js';
import {
  ActionLink,
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Select,
  Textarea,
} from '../components/ui/index.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';
import './DesignSystem.css';

const COLORS = [
  { name: 'Canvas', token: '--ds-canvas', hex: '#FBFBFC' },
  { name: 'Surface', token: '--ds-surface', hex: '#FFFFFF' },
  { name: 'Text', token: '--ds-text', hex: '#14161A' },
  { name: 'Muted text', token: '--ds-text-muted', hex: '#667085' },
  { name: 'Border', token: '--ds-border', hex: '#E7E9EE' },
  { name: 'Action', token: '--ds-action', hex: '#4F46E5' },
  { name: 'Success', token: '--color-ok', hex: '#067647' },
  { name: 'Danger', token: '--color-danger', hex: '#D92D20' },
] as const;

const SPACES = [
  { step: 1, pixels: 4 },
  { step: 2, pixels: 8 },
  { step: 3, pixels: 12 },
  { step: 4, pixels: 16 },
  { step: 5, pixels: 24 },
  { step: 6, pixels: 32 },
  { step: 8, pixels: 48 },
] as const;

export function DesignSystemPage(): JSX.Element {
  const t = useT();
  const [sampleName, setSampleName] = useState('');

  useDocumentMeta({
    title: t({ ko: '디자인 시스템', en: 'Design system' }),
    description: t({
      ko: 'CloudBTL 제품의 색상, 타이포그래피, 간격과 UI 컴포넌트 사용 기준입니다.',
      en: 'CloudBTL product foundations, components, and interface guidelines.',
    }),
    canonical: '/design-system',
  });

  return (
    <Shell>
      <article className="design-system-page">
        <header className="design-system-hero">
          <div className="design-system-hero__meta">
            <span className="design-system-kicker">CloudBTL Design System</span>
            <Badge tone="accent">v0.1</Badge>
          </div>
          <h1>
            {t({ ko: '조용하고 명확한 업무 인터페이스', en: 'Quiet, clear work interfaces' })}
          </h1>
          <p>
            {t({
              ko: '문서가 주인공이 되도록 장식은 줄이고, 상태와 행동은 분명하게 표현합니다. 이 페이지가 제품 UI의 기준이자 살아 있는 예제입니다.',
              en: 'Keep decoration quiet so the document stays central, while making status and actions unmistakable. This page is the product UI reference and living example.',
            })}
          </p>
          <nav
            className="design-system-nav"
            aria-label={t({ ko: '디자인 시스템 섹션', en: 'Design system sections' })}
          >
            <a href="#foundations">{t({ ko: '기반', en: 'Foundations' })}</a>
            <a href="#components">{t({ ko: '컴포넌트', en: 'Components' })}</a>
            <a href="#principles">{t({ ko: '원칙', en: 'Principles' })}</a>
          </nav>
        </header>

        <section
          id="foundations"
          className="design-system-section"
          aria-labelledby="foundations-title"
        >
          <div className="design-system-section__heading">
            <span>01</span>
            <div>
              <h2 id="foundations-title">{t({ ko: '기반', en: 'Foundations' })}</h2>
              <p>
                {t({
                  ko: '일관성을 만드는 가장 작은 결정들',
                  en: 'The smallest decisions that create consistency',
                })}
              </p>
            </div>
          </div>

          <div className="design-system-block">
            <h3>{t({ ko: '색상', en: 'Color' })}</h3>
            <p className="design-system-help">
              {t({
                ko: '인디고는 주요 행동과 선택에만, 상태 색상은 실제 의미가 있을 때만 사용합니다.',
                en: 'Use indigo only for primary action and selection; use semantic colors only when they carry meaning.',
              })}
            </p>
            <div className="design-system-swatches">
              {COLORS.map((color) => (
                <Card key={color.token} padding="none" className="design-system-swatch">
                  <span
                    className="design-system-swatch__color"
                    style={{ background: `var(${color.token})` }}
                    aria-hidden="true"
                  />
                  <span className="design-system-swatch__label">
                    <strong>{color.name}</strong>
                    <code>{color.token}</code>
                    <small>{color.hex}</small>
                  </span>
                </Card>
              ))}
            </div>
          </div>

          <div className="design-system-foundation-grid">
            <Card padding="md" className="design-system-type-card">
              <h3>{t({ ko: '타이포그래피', en: 'Typography' })}</h3>
              <div className="design-system-type-sample design-system-type-sample--display">
                Aa 문서 공유
              </div>
              <div className="design-system-type-sample design-system-type-sample--title">
                Aa 읽기 깊이 분석
              </div>
              <div className="design-system-type-sample design-system-type-sample--body">
                {t({
                  ko: '본문은 15px을 기본으로 충분한 행간을 둡니다.',
                  en: 'Body copy starts at 15px with comfortable leading.',
                })}
              </div>
              <code className="design-system-type-sample design-system-type-sample--mono">
                Authorization: Bearer assertion
              </code>
            </Card>

            <Card padding="md">
              <h3>{t({ ko: '간격과 형태', en: 'Spacing and shape' })}</h3>
              <div className="design-system-spacing-list">
                {SPACES.map(({ step, pixels }) => (
                  <div key={step}>
                    <code>space-{step}</code>
                    <span style={{ width: `var(--space-${step})` }} aria-hidden="true" />
                    <small>{pixels}px</small>
                  </div>
                ))}
              </div>
              <div
                className="design-system-radii"
                aria-label={t({ ko: '모서리 반경', en: 'Corner radii' })}
              >
                <span className="design-system-radius design-system-radius--sm">8</span>
                <span className="design-system-radius design-system-radius--md">12</span>
                <span className="design-system-radius design-system-radius--lg">16</span>
              </div>
            </Card>
          </div>
        </section>

        <section
          id="components"
          className="design-system-section"
          aria-labelledby="components-title"
        >
          <div className="design-system-section__heading">
            <span>02</span>
            <div>
              <h2 id="components-title">{t({ ko: '컴포넌트', en: 'Components' })}</h2>
              <p>
                {t({
                  ko: '접근성과 상태 표현을 내장한 조립 단위',
                  en: 'Building blocks with accessibility and state built in',
                })}
              </p>
            </div>
          </div>

          <div className="design-system-component-grid">
            <Card padding="md" className="design-system-demo">
              <div className="design-system-demo__head">
                <h3>Button</h3>
                <code>components/ui</code>
              </div>
              <div className="design-system-demo__row">
                <Button variant="primary">{t({ ko: '문서 업로드', en: 'Upload document' })}</Button>
                <Button>{t({ ko: '취소', en: 'Cancel' })}</Button>
                <Button variant="ghost">{t({ ko: '자세히', en: 'Details' })}</Button>
                <Button variant="danger">{t({ ko: '삭제', en: 'Delete' })}</Button>
              </div>
              <div className="design-system-demo__row">
                <Button size="sm">Small</Button>
                <Button disabled>{t({ ko: '사용 불가', en: 'Disabled' })}</Button>
                <Button loading>{t({ ko: '저장 중', en: 'Saving' })}</Button>
                <ActionLink to="/plans" variant="primary">
                  {t({ ko: '플랜 보기', en: 'View plans' })}
                </ActionLink>
              </div>
            </Card>

            <Card padding="md" className="design-system-demo">
              <div className="design-system-demo__head">
                <h3>Badge</h3>
                <code>tone</code>
              </div>
              <div className="design-system-demo__row">
                <Badge>Draft</Badge>
                <Badge tone="accent">Pro</Badge>
                <Badge tone="success">Active</Badge>
                <Badge tone="warning">Trial</Badge>
                <Badge tone="danger">Expired</Badge>
              </div>
            </Card>

            <Card padding="md" className="design-system-demo design-system-demo--wide">
              <div className="design-system-demo__head">
                <h3>Alert</h3>
                <code>tone</code>
              </div>
              <div className="design-system-alerts">
                <Alert tone="info" title={t({ ko: '참고', en: 'Note' })}>
                  {t({
                    ko: '공유 링크는 방문자를 자동으로 기록합니다.',
                    en: 'Share links record visitors automatically.',
                  })}
                </Alert>
                <Alert tone="success" title={t({ ko: '업로드 완료', en: 'Upload complete' })}>
                  {t({
                    ko: '문서를 공유할 준비가 되었습니다.',
                    en: 'Your document is ready to share.',
                  })}
                </Alert>
                <Alert tone="warning" title={t({ ko: '체험 종료 예정', en: 'Trial ending' })}>
                  {t({
                    ko: '3일 뒤 Free 플랜으로 전환됩니다.',
                    en: 'Your workspace moves to Free in 3 days.',
                  })}
                </Alert>
                <Alert tone="danger" title={t({ ko: '저장하지 못함', en: 'Could not save' })}>
                  {t({
                    ko: '연결을 확인하고 다시 시도하세요.',
                    en: 'Check your connection and try again.',
                  })}
                </Alert>
              </div>
            </Card>

            <Card padding="md" className="design-system-demo design-system-demo--wide">
              <div className="design-system-demo__head">
                <h3>Field</h3>
                <code>label + control + help</code>
              </div>
              <form className="design-system-form" onSubmit={(event) => event.preventDefault()}>
                <Field
                  label={t({ ko: '워크스페이스 이름', en: 'Workspace name' })}
                  htmlFor="ds-workspace-name"
                  hint={t({
                    ko: '팀원이 알아보기 쉬운 이름을 사용하세요.',
                    en: 'Use a name your team will recognize.',
                  })}
                  required
                >
                  <Input
                    id="ds-workspace-name"
                    value={sampleName}
                    onChange={(event) => setSampleName(event.target.value)}
                    placeholder="CloudBTL Studio"
                  />
                </Field>
                <Field label={t({ ko: '기본 역할', en: 'Default role' })} htmlFor="ds-role">
                  <Select id="ds-role" defaultValue="member">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </Select>
                </Field>
                <Field
                  label={t({ ko: '설명', en: 'Description' })}
                  htmlFor="ds-description"
                  error={t({
                    ko: '예시 오류 메시지입니다.',
                    en: 'This is an example error message.',
                  })}
                >
                  <Textarea id="ds-description" defaultValue="CloudBTL" />
                </Field>
              </form>
            </Card>
          </div>
        </section>

        <section
          id="principles"
          className="design-system-section"
          aria-labelledby="principles-title"
        >
          <div className="design-system-section__heading">
            <span>03</span>
            <div>
              <h2 id="principles-title">{t({ ko: '제품 원칙', en: 'Product principles' })}</h2>
              <p>
                {t({
                  ko: '새 화면을 만들 때 판단하는 순서',
                  en: 'How to make decisions when building a new screen',
                })}
              </p>
            </div>
          </div>
          <div className="design-system-principles">
            {[
              {
                no: '01',
                title: t({ ko: '문서가 먼저', en: 'Document first' }),
                body: t({
                  ko: 'UI는 콘텐츠를 돕고, 경쟁하지 않습니다.',
                  en: 'The interface supports content and never competes with it.',
                }),
              },
              {
                no: '02',
                title: t({ ko: '행동은 하나씩', en: 'One clear action' }),
                body: t({
                  ko: '한 영역에는 하나의 주요 행동만 강조합니다.',
                  en: 'Emphasize one primary action within each area.',
                }),
              },
              {
                no: '03',
                title: t({ ko: '상태는 색만으로 말하지 않기', en: 'Never rely on color alone' }),
                body: t({
                  ko: '텍스트, 형태, 접근성 속성을 함께 제공합니다.',
                  en: 'Pair color with text, shape, and accessible semantics.',
                }),
              },
              {
                no: '04',
                title: t({ ko: '한국어와 영어를 함께', en: 'Designed for Korean and English' }),
                body: t({
                  ko: '문구 길이가 달라도 레이아웃이 버텨야 합니다.',
                  en: 'Layouts must hold when copy length changes between languages.',
                }),
              },
            ].map((principle) => (
              <Card key={principle.no} padding="md">
                <span>{principle.no}</span>
                <h3>{principle.title}</h3>
                <p>{principle.body}</p>
              </Card>
            ))}
          </div>
        </section>
      </article>
    </Shell>
  );
}
